<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  categories: {
    type: Array,
    required: true,
    validator: (value) => value.every(item => typeof item === 'string'),
  },
  data: {
    type: Array,
    required: true,
    validator: (value) =>
      value.every(item =>
        item.label &&
        Array.isArray(item.scores) &&
        item.scores.every(s => s === null || s === undefined || typeof s === 'number')
      ),
  },
  chartType: {
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value),
  },
  yAxisMin: {
    type: Number,
    default: 1000,
  },
  yAxisMax: {
    type: Number,
    default: 2000,
  },
  chartHeight: {
    type: String,
    default: '160px',
  },
});

const LITERACY_COLOR = '#6484F0';
const NUMERACY_COLOR = '#F88082';

const CHART_GLOBAL_PADDING = {
  TOP: 0,
  RIGHT: 0,
  BOTTOM: 0,
  LEFT: 0,
};
const Y_AXIS_LABEL_WIDTH = 60;
const X_AXIS_LABEL_HEIGHT = 30;

const SVG_VIEW_WIDTH_VIRTUAL = 600;
const SVG_VIEW_HEIGHT_VIRTUAL = 300;

const SVG_VIEW_WIDTH = SVG_VIEW_WIDTH_VIRTUAL - Y_AXIS_LABEL_WIDTH - CHART_GLOBAL_PADDING.RIGHT;
const SVG_VIEW_HEIGHT = SVG_VIEW_HEIGHT_VIRTUAL - X_AXIS_LABEL_HEIGHT - CHART_GLOBAL_PADDING.TOP;

const AXIS_PADDING = {
  LEFT: 0,
  RIGHT: 0,
  TOP: 35,
  BOTTOM: 35,
};

const DRAWING_WIDTH = SVG_VIEW_WIDTH - AXIS_PADDING.LEFT - AXIS_PADDING.RIGHT;
const DRAWING_HEIGHT = SVG_VIEW_HEIGHT - AXIS_PADDING.TOP - AXIS_PADDING.BOTTOM;

const yAxisRange = computed(() => props.yAxisMax - props.yAxisMin);

const xAxisPoints = computed(() => {
  const points = [];
  const count = props.categories.length;
  if (count <= 1) {
    if (count === 1) points.push({ category: props.categories[0], x: AXIS_PADDING.LEFT + DRAWING_WIDTH / 2, index: 0 });
    return points;
  }

  for (let i = 0; i < count; i++) {
    const x = AXIS_PADDING.LEFT + DRAWING_WIDTH * (i / (count - 1));
    points.push({ category: props.categories[i], x, index: i });
  }
  return points;
});

const getSvgY = (score) => {
  if (score === null || score === undefined || isNaN(score)) return null;
  const scoreHeight = ((score - props.yAxisMin) / yAxisRange.value) * DRAWING_HEIGHT;
  return SVG_VIEW_HEIGHT - AXIS_PADDING.BOTTOM - scoreHeight;
};

const getSvgX = (index) => {
  const count = props.categories.length;
  if (count <= 1) return AXIS_PADDING.LEFT + DRAWING_WIDTH / 2;
  return AXIS_PADDING.LEFT + DRAWING_WIDTH * (index / (count - 1));
};

const processedLineData = computed(() => {
  if (!props.data || !Array.isArray(props.data)) return [];

  const assignedColor =
    props.chartType === 'literacy' ? LITERACY_COLOR : NUMERACY_COLOR;

  return props.data.map(lineItem => {
    const scoresToProcess = Array.isArray(lineItem.scores)
      ? lineItem.scores
      : [];

    const processedPoints = scoresToProcess
      .map((score, index) => {
        const svgX = getSvgX(index);
        const svgY = getSvgY(score);
        return svgY === null ? null : { score, svgX, svgY, index };
      })
      .filter(Boolean);

    let pathD = '';
    if (processedPoints.length > 0) {
      pathD += `M ${processedPoints[0].svgX} ${processedPoints[0].svgY}`;
      for (let i = 1; i < processedPoints.length; i++) {
        const prev = processedPoints[i - 1];
        const cur = processedPoints[i];
        if (cur.index !== prev.index + 1) {
          pathD += ` M ${cur.svgX} ${cur.svgY}`;
        } else {
          pathD += ` L ${cur.svgX} ${cur.svgY}`;
        }
      }
    }

    return {
      label: lineItem.label,
      color: assignedColor,
      pathD,
      points: processedPoints,
    };
  });
});
</script>

<template>
  <div class="trend-line-chart-wrapper">
    <div class="legend-wrap">
      <div class="legend-item">
        <div
          class="legend-color-box" :class="chartType">
          {{ chartType === 'literacy' ? '문해력' : '수리력' }}
        </div>
      </div>
    </div>
    <div class="chart-container">
      <!-- Y축 라벨 -->
      <div class="y-axis-labels" :style="{ height: chartHeight }">
        <p class="y-axis-label" style="bottom: 0%">{{ yAxisMin }}</p>
        <p class="y-axis-label" style="bottom: 100%">{{ yAxisMax }}</p>
      </div>

      <div class="chart-wrap" :class="props.chartType">
        <div class="svg-wrap" :style="{ height: chartHeight }">
          <svg
            :viewBox="`0 0 ${SVG_VIEW_WIDTH} ${SVG_VIEW_HEIGHT}`"
            class="line-chart-svg" :style="{ height: chartHeight }"
          >
            <g :transform="`translate(0,0)`">
              <!-- 데이터 라인 -->
              <g v-for="lineItem in processedLineData" :key="lineItem.label">
                <path
                  :d="lineItem.pathD"
                  :stroke="lineItem.color"
                  stroke-width="3"
                  fill="none"
                />
                <!-- 데이터 포인트 -->
                <circle
                  v-for="point in lineItem.points"
                  :key="`${lineItem.label}-${point.index}`"
                  :cx="point.svgX"
                  :cy="point.svgY"
                  :fill="lineItem.color"                 
                  r="8"
                  class="data-point"
                />
                <!-- 점수 텍스트 -->
                <text
                  v-for="point in lineItem.points"
                  :key="`text-${lineItem.label}-${point.index}`"
                  :x="point.svgX - 2.5"
                  :y="point.svgY - 25"
                  fill="#000"
                  font-size="20"
                  font-weight="600"
                  text-anchor="middle"
                  dominant-baseline="middle"
                >
                  {{ point.score }}
                </text>
              </g>
            </g>
          </svg>
        </div>
        <div class="x-axis-labels">
          <p
            v-for="point in xAxisPoints"
            :key="`x-label-${point.category}`"
            class="x-axis-label">
            {{ point.category }}
          </p>
        </div>        
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trend-line-chart-wrapper {
  width: 100%; 
  margin: 0 auto;
  user-select: none;
  padding: 10px 20px 10px 25px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.chart-container {
  display: flex;
  align-items: flex-start;
}
.y-axis-labels {
  position: relative;
  width: 50px;
  font-size: 16px;
  color: #000;
  user-select: none;
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  padding-top: 15px;
}
.chart-wrap {
  flex: 1;
  position: relative;
  padding-top: 10px;
}
.svg-wrap {
  position: relative;
  width: 100%;
  overflow: visible;
  border-bottom: 2px solid #000;
  border-left: 2px solid #000;
  svg {width: 100%;}
}
.data-point {
  cursor: pointer;
  transition: r 0.2s ease;
}
.data-point:hover {
  r: 7;
}
text {
  user-select: none;
}
.x-axis-labels {
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: 5px 50px 0;
  font-size: 16px;
}
.legend-wrap {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: flex-end;
  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #333;
  }
  .legend-color-box {
    border-radius: 3px;
    border: 1px solid;
    border-radius: 5px;
    height: 25px;
    width: 150px;
    line-height: 25px;
    text-align: center;
    font-size: 16px;
    &.literacy {
      color: #4362CC;
      border-color: #4362CC;
    }
     &.numeracy {
      color: #CE4E56;
      border-color: #CE4E56;
    }
  }
}
</style>
